import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useUserRole } from '../lib/queries/useUserRole';

/**
 * Route-level guard for admin surfaces (CoachInvites, CoachReview).
 * Mirrors `ParentOnlyRoute`.
 *
 *   - Loading: render nothing (avoid flashing the wrong surface).
 *   - SUPER_ADMIN: render children.
 *   - Any other role: redirect to `fallback` (default '/').
 *
 * The `is_admin()` SQL helper (migration 00070) gates the underlying
 * coach_review_invites / coach_review_log tables, so a non-admin who
 * somehow bypasses this guard still can't read or write rows.
 */
export function AdminOnlyRoute({
  children,
  fallback = '/',
}: {
  children: ReactNode;
  fallback?: string;
}) {
  const { role, loading, error } = useUserRole();
  // Show a visible "checking access…" state instead of a blank page so
  // a hung role-fetch surfaces clearly. Helps debug auth/RLS issues.
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui, sans-serif', color: '#666' }}>
        Checking admin access…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 40, fontFamily: 'system-ui, sans-serif', color: '#c0392b' }}>
        <h2>Couldn't check your access</h2>
        <p>{error}</p>
        <p style={{ opacity: 0.7, marginTop: 12 }}>
          Try signing in again from <a href="/">home</a>.
        </p>
      </div>
    );
  }
  if (role !== 'SUPER_ADMIN') return <Navigate to={fallback} replace />;
  return <>{children}</>;
}
